import { forwardRef, useId, type InputHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Validation / help text. When `error` is set it renders in the danger color. */
  hint?: string;
  /** Renders `hint` as a caution rather than as neutral help text. */
  warn?: boolean;
  error?: string;
  /** Wrapper class (the field group), not the <input> itself. */
  containerClassName?: string;
}

export const fieldControl =
  'block w-full rounded-xl border bg-surface px-3 py-2 text-sm text-surface-dark placeholder:text-surface-dark/40 shadow-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 dark:bg-surface-dark-subtle dark:text-surface dark:placeholder:text-surface/40';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, warn, error, className, containerClassName, id, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const describedBy = error || hint ? `${inputId}-desc` : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-sm font-medium text-surface-dark dark:text-surface"
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          fieldControl,
          error
            ? 'border-danger focus:border-danger focus:ring-danger/30'
            : 'border-surface-border dark:border-surface-dark-border',
          className,
        )}
        {...rest}
      />
      {(error || hint) && (
        <p
          id={describedBy}
          className={cn(
            'text-xs',
            error
              ? 'text-danger'
              : warn
                ? 'font-medium text-[#8A6A00] dark:text-warning'
                : 'text-surface-dark/60 dark:text-surface/60',
          )}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
